import { useMemo, useState, type ReactNode } from 'react';
import {
  BarChart3,
  CalendarCheck2,
  ClipboardCheck,
  Download,
  FileStack,
  FileText,
  LineChart,
  PhoneOff,
  PoundSterling,
  Printer,
  ShieldCheck,
  TrendingDown,
  Users,
  WalletCards,
} from 'lucide-react';

type SavingCategory =
  | 'Scheduling'
  | 'Visits'
  | 'Administration'
  | 'Payroll'
  | 'Billing'
  | 'Family'
  | 'Reporting'
  | 'Compliance'
  | 'Documents'
  | 'Capacity';

type SavingPeriod = 'week' | 'month' | 'year';

interface SavingLever {
  id: string;
  title: string;
  category: SavingCategory;
  currentCost: number;
  targetCost: number;
  timeSavedHours: number;
  mechanism: string;
  action: string;
  linkedModule: string;
}

const savingLevers: SavingLever[] = [
  {
    id: 'reduce-overtime',
    title: 'Reduce overtime with intelligent scheduling',
    category: 'Scheduling',
    currentCost: 1880,
    targetCost: 960,
    timeSavedHours: 8,
    mechanism: 'Schedule optimizer recommends carers by distance, availability, skills, continuity and overtime risk before rota confirmation.',
    action: 'Open Schedule Optimizer and approve lower-cost assignments before publishing rota.',
    linkedModule: '/schedule-optimizer',
  },
  {
    id: 'reduce-missed-visits',
    title: 'Reduce missed visits',
    category: 'Visits',
    currentCost: 1450,
    targetCost: 520,
    timeSavedHours: 6,
    mechanism: 'Live carer telemetry, late check-in reminders and admin alerts catch missed visits before they become service failures.',
    action: 'Monitor Live Carers and Automations critical visit reminders.',
    linkedModule: '/live-carers',
  },
  {
    id: 'reduce-admin-time',
    title: 'Reduce administration time',
    category: 'Administration',
    currentCost: 3200,
    targetCost: 1780,
    timeSavedHours: 42,
    mechanism: 'Admin AI, command bar, daily briefing, automatic reports and smart search remove repetitive manual checking.',
    action: 'Use Admin AI for daily briefing, urgent alerts and natural-language report generation.',
    linkedModule: '/admin-ai',
  },
  {
    id: 'reduce-payroll-errors',
    title: 'Reduce payroll errors',
    category: 'Payroll',
    currentCost: 1180,
    targetCost: 260,
    timeSavedHours: 10,
    mechanism: 'Payroll is calculated from check-in/check-out, hourly rates and rota variance checks instead of manual spreadsheets.',
    action: 'Review Carer Pay variance alerts before generating weekly/monthly PDF payroll.',
    linkedModule: '/carer-payroll',
  },
  {
    id: 'reduce-forgotten-invoices',
    title: 'Reduce forgotten invoices',
    category: 'Billing',
    currentCost: 2450,
    targetCost: 420,
    timeSavedHours: 12,
    mechanism: 'Automatic billing from completed visits flags unbilled visits, unpaid invoices and revenue vs cost.',
    action: 'Open Billing and review automatic invoices plus unpaid invoice reminders.',
    linkedModule: '/billing',
  },
  {
    id: 'reduce-family-calls',
    title: 'Reduce repetitive family calls',
    category: 'Family',
    currentCost: 960,
    targetCost: 310,
    timeSavedHours: 18,
    mechanism: 'Family portal sends family-safe visit updates, health summaries, messages and permitted documents.',
    action: 'Keep family updates and permitted documents current to reduce repetitive office calls.',
    linkedModule: '/family-portal',
  },
  {
    id: 'reduce-reporting-time',
    title: 'Reduce time spent on reporting',
    category: 'Reporting',
    currentCost: 1720,
    targetCost: 380,
    timeSavedHours: 28,
    mechanism: 'Advanced reports generate financial, operational, payroll, compliance and incident dashboards with PDF/CSV exports.',
    action: 'Use Reporting Command Centre and export PDF/CSV instead of manually compiling reports.',
    linkedModule: '/analytics/reports',
  },
  {
    id: 'reduce-cqc-risk',
    title: 'Reduce CQC non-compliance risk',
    category: 'Compliance',
    currentCost: 4200,
    targetCost: 1250,
    timeSavedHours: 16,
    mechanism: 'Compliance dashboard tracks Q-Score, PIR draft, evidence gaps, audit trail, incidents, complaints and reminders.',
    action: 'Resolve compliance gaps before inspections or PIR preparation.',
    linkedModule: '/compliance',
  },
  {
    id: 'reduce-paper-costs',
    title: 'Reduce paper and manual document costs',
    category: 'Documents',
    currentCost: 680,
    targetCost: 140,
    timeSavedHours: 9,
    mechanism: 'PDF generation, download/print controls, document logs and digital evidence reduce manual printing and filing.',
    action: 'Generate PDFs only when needed and keep digital evidence in the audit trail.',
    linkedModule: '/admin-error-guard',
  },
  {
    id: 'increase-admin-capacity',
    title: 'Increase capacity without increasing office workload',
    category: 'Capacity',
    currentCost: 5100,
    targetCost: 2600,
    timeSavedHours: 55,
    mechanism: 'Automation, AI summaries, admin guard, family portal and reporting let the same admin team manage more clients safely.',
    action: 'Track weekly admin hours saved and use dashboards to prioritise exceptions, not routine checking.',
    linkedModule: '/automations',
  },
];

const categoryIcons: Record<SavingCategory, ReactNode> = {
  Scheduling: <CalendarCheck2 className="h-5 w-5" />,
  Visits: <ClipboardCheck className="h-5 w-5" />,
  Administration: <LineChart className="h-5 w-5" />,
  Payroll: <WalletCards className="h-5 w-5" />,
  Billing: <PoundSterling className="h-5 w-5" />,
  Family: <PhoneOff className="h-5 w-5" />,
  Reporting: <BarChart3 className="h-5 w-5" />,
  Compliance: <ShieldCheck className="h-5 w-5" />,
  Documents: <FileStack className="h-5 w-5" />,
  Capacity: <Users className="h-5 w-5" />,
};

const periodMultiplier: Record<SavingPeriod, number> = {
  week: 0.25,
  month: 1,
  year: 12,
};

export function MoneySavingsDashboard() {
  const [period, setPeriod] = useState<SavingPeriod>('month');
  const [category, setCategory] = useState<'All' | SavingCategory>('All');

  const visibleLevers = useMemo(
    () => savingLevers.filter((item) => category === 'All' || item.category === category),
    [category]
  );
  const multiplier = periodMultiplier[period];
  const totals = useMemo(() => {
    const currentCost = visibleLevers.reduce((sum, item) => sum + item.currentCost * multiplier, 0);
    const targetCost = visibleLevers.reduce((sum, item) => sum + item.targetCost * multiplier, 0);
    const savings = currentCost - targetCost;
    const hours = visibleLevers.reduce((sum, item) => sum + item.timeSavedHours * multiplier, 0);
    return { currentCost, targetCost, savings, hours };
  }, [visibleLevers, multiplier]);

  const downloadCsv = () => {
    const rows = [
      ['Saving area', 'Category', 'Current cost', 'Target cost', 'Estimated saving', 'Time saved hours', 'Mechanism', 'Action'],
      ...visibleLevers.map((item) => [
        item.title,
        item.category,
        money(item.currentCost * multiplier),
        money(item.targetCost * multiplier),
        money((item.currentCost - item.targetCost) * multiplier),
        `${Math.round(item.timeSavedHours * multiplier * 10) / 10}`,
        item.mechanism,
        item.action,
      ]),
    ];
    downloadBlob(
      new Blob([rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }),
      `fuchsia-money-savings-${period}.csv`
    );
  };

  const printSummary = () => {
    window.print();
  };

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <PoundSterling className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">Direct Money Savings</p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Money Savings Dashboard</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Shows where the application can directly reduce costs: overtime, missed visits, admin time,
                payroll errors, forgotten invoices, family calls, reporting work, CQC risk, paper and admin capacity.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={period}
              onChange={(event) => setPeriod(event.target.value as SavingPeriod)}
              className="h-10 rounded-md border border-slate-300 px-3 text-sm"
              aria-label="Savings period"
            >
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
            <button
              type="button"
              onClick={downloadCsv}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              type="button"
              onClick={printSummary}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Printer className="h-4 w-4" />
              Print summary
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Estimated saving" value={money(totals.savings)} icon={<TrendingDown className="h-5 w-5" />} tone="success" />
        <MetricCard label="Current avoidable cost" value={money(totals.currentCost)} icon={<PoundSterling className="h-5 w-5" />} tone="warning" />
        <MetricCard label="Target cost" value={money(totals.targetCost)} icon={<ShieldCheck className="h-5 w-5" />} />
        <MetricCard label="Admin hours saved" value={`${Math.round(totals.hours)}h`} icon={<Users className="h-5 w-5" />} tone="success" />
      </section>

      <section className="grid gap-4 xl:grid-cols-[260px_1fr]">
        <aside className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-bold text-slate-950">Filter by saving area</p>
          <div className="mt-4 space-y-2">
            {(['All', ...Object.keys(categoryIcons)] as Array<'All' | SavingCategory>).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-semibold ${
                  category === item ? 'bg-[#6c3a80] text-white' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span>{item}</span>
                {item !== 'All' && <span>{categoryIcons[item]}</span>}
              </button>
            ))}
          </div>
        </aside>

        <section className="grid gap-4 xl:grid-cols-2">
          {visibleLevers.map((lever) => {
            const saving = (lever.currentCost - lever.targetCost) * multiplier;
            const current = lever.currentCost * multiplier;
            const target = lever.targetCost * multiplier;
            const hours = lever.timeSavedHours * multiplier;
            const percent = Math.round((saving / current) * 100);

            return (
              <article key={lever.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-3">
                    <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
                      {categoryIcons[lever.category]}
                    </div>
                    <div>
                      <h2 className="font-bold text-slate-950">{lever.title}</h2>
                      <p className="mt-1 text-sm text-slate-500">{lever.category}</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800">
                    Save {percent}%
                  </span>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <SmallStat label="Current" value={money(current)} />
                  <SmallStat label="Target" value={money(target)} />
                  <SmallStat label="Saving" value={money(saving)} positive />
                </div>

                <div className="mt-4 rounded-md bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                  <p><span className="font-semibold text-slate-950">How:</span> {lever.mechanism}</p>
                  <p className="mt-2"><span className="font-semibold text-slate-950">Admin action:</span> {lever.action}</p>
                  <p className="mt-2 font-semibold text-[#6c3a80]">Estimated admin time saved: {Math.round(hours * 10) / 10}h per {period}</p>
                </div>

                <a
                  href={lever.linkedModule}
                  className="mt-4 inline-flex h-10 items-center justify-center rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Open linked module
                </a>
              </article>
            );
          })}
        </section>
      </section>

      <section className="rounded-lg border border-blue-200 bg-blue-50 p-5">
        <div className="flex gap-3 text-sm leading-6 text-blue-950">
          <FileText className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            These figures are planning estimates for showing business value. In production, the values should be calculated from verified database records: rota, payroll, invoices, family messages, reports, CQC evidence, document exports and admin activity logs.
          </p>
        </div>
      </section>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: ReactNode;
  tone?: 'default' | 'success' | 'warning';
}) {
  const styles = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
  };

  return (
    <article className={`rounded-lg border p-4 shadow-sm ${styles[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </article>
  );
}

function SmallStat({ label, value, positive = false }: { label: string; value: string; positive?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${positive ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className={`mt-1 text-lg font-bold ${positive ? 'text-emerald-800' : 'text-slate-950'}`}>{value}</p>
    </div>
  );
}

function money(value: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(value);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
